/** Heal socket + game room after tab background, bfcache restore, or network return. */

import {
  SOCKET_PING_INTERVAL_MS,
  SOCKET_PING_TIMEOUT_MS,
} from "@cold-case/shared/constants";

export type ConnectionHealContext = {
  getSessionId: () => string;
  getSocket: () => import("socket.io-client").Socket | null;
  onHeal: () => void;
  onBackground?: () => void;
  /** Tab close / navigation away — use keepalive fetch for best-effort cleanup. */
  onPageHide?: () => void;
  onOffline?: () => void;
};

let wired = false;

export function wireConnectionLifecycle(ctx: ConnectionHealContext): void {
  if (wired) return;
  wired = true;

  const heal = (): void => {
    if (!ctx.getSessionId()) return;
    const sock = ctx.getSocket();
    if (sock && !sock.connected) sock.connect();
    ctx.onHeal();
  };

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      ctx.onBackground?.();
      return;
    }
    heal();
  });

  window.addEventListener("pagehide", () => {
    ctx.onPageHide?.();
    ctx.onBackground?.();
  });

  window.addEventListener("pageshow", () => {
    heal();
  });

  window.addEventListener("online", heal);
  window.addEventListener("offline", () => {
    ctx.onOffline?.();
  });
}

/** Socket.IO client options tuned for mobile background / flaky networks. */
export function gameSocketOptions(token: string): {
  auth: { token: string };
  path: string;
  reconnection: boolean;
  reconnectionAttempts: number;
  reconnectionDelay: number;
  reconnectionDelayMax: number;
  pingInterval: number;
  pingTimeout: number;
} {
  return {
    auth: { token },
    path: "/socket.io",
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 8000,
    pingInterval: SOCKET_PING_INTERVAL_MS,
    pingTimeout: SOCKET_PING_TIMEOUT_MS,
  };
}
